/**
 * JobManagementWizard — Guided step-by-step wizard for job management setup.
 *
 * Linear flow: each step must be explicitly completed before advancing.
 * Users can go back to previous steps but cannot skip ahead.
 */

import { Button } from '@/shared/ui/button';
import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import AssignTasksStep from './AssignTasksStep';
import JobCategoriesStep from './JobCategoriesStep';
import ReviewStep from './ReviewStep';
import TaskTypesStep from './TaskTypesStep';

const STEPS = [
  {
    number: 1,
    label: 'Job Categories',
    description: 'Create and manage the job category types your organisation uses.',
  },
  {
    number: 2,
    label: 'Task Types',
    description: 'Define the task types that can be assigned to job categories and link forms.',
  },
  {
    number: 3,
    label: 'Assign Tasks',
    description: 'Link task types to each job category and set the order they appear.',
  },
  {
    number: 4,
    label: 'Review',
    description: 'Review your configuration before finishing.',
  },
] as const;

export default function JobManagementWizard() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());

  const markCompleteAndAdvance = () => {
    setCompletedSteps((prev) => new Set(prev).add(activeStep));
    if (activeStep < STEPS.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const goBack = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  const goToCompletedStep = (idx: number) => {
    if (completedSteps.has(idx) || idx === activeStep) {
      setActiveStep(idx);
    }
  };

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <div className="space-y-6">
      {/* Step Indicator */}
      <div className="flex items-center justify-center">
        {STEPS.map((step, idx) => {
          const isActive = idx === activeStep;
          const isCompleted = completedSteps.has(idx);
          const isClickable = isCompleted || idx === activeStep;

          return (
            <div key={step.number} className="flex items-center">
              <button
                type="button"
                onClick={() => goToCompletedStep(idx)}
                disabled={!isClickable}
                className={`flex flex-col items-center gap-1.5 ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
                    isActive
                      ? 'text-white'
                      : isCompleted
                        ? 'bg-green-100 text-green-700 border-2 border-green-500'
                        : 'bg-muted text-muted-foreground border-2 border-border'
                  }`}
                  style={isActive ? { backgroundColor: '#E84A2B' } : undefined}
                >
                  {isCompleted && !isActive ? <Check className="w-4 h-4" /> : step.number}
                </div>
                <div className="text-center">
                  <span
                    className={`text-xs font-medium block ${
                      isActive
                        ? 'text-foreground'
                        : isCompleted
                          ? 'text-green-700'
                          : 'text-muted-foreground'
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              </button>

              {idx < STEPS.length - 1 && (
                <div
                  className={`w-16 h-0.5 mx-2 mt-[-18px] transition-colors ${
                    completedSteps.has(idx) ? 'bg-green-500' : 'bg-border'
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Step Content */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="mb-5">
          <h2 className="text-lg font-semibold">
            Step {STEPS[activeStep].number}: {STEPS[activeStep].label}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">{STEPS[activeStep].description}</p>
        </div>

        {activeStep === 0 && <JobCategoriesStep />}
        {activeStep === 1 && <TaskTypesStep />}
        {activeStep === 2 && <AssignTasksStep />}
        {activeStep === 3 && <ReviewStep />}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={goBack} disabled={activeStep === 0}>
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        <span className="text-xs text-muted-foreground">
          Step {activeStep + 1} of {STEPS.length}
        </span>

        {isLastStep ? (
          <Button
            onClick={() => navigate('/settings')}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            <Check className="w-4 h-4 mr-1" />
            Finish
          </Button>
        ) : (
          <Button
            onClick={markCompleteAndAdvance}
            className="text-white"
            style={{ backgroundColor: '#E84A2B' }}
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
